'use client';
import { useParams } from 'next/navigation';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { SubHeadline, Text } from '~components/Typography';
import { useAccountContext } from '~context/account';
import { usePaywallContext } from '~context/paywall';
import { NsfwReason, PaymentReason, RegisterInbound } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useLanguage } from '~features/language';
import { useSwitchSafeModeWithAgeValidation } from '~features/nsfw';
import { useIsDuplicateOwner } from '~hooks/useIsDuplicateOwner';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import LinkIcon from '~public/assets/icons/navigation/link.svg';
import LogoutIcon from '~public/assets/icons/navigation/logout.svg';
import ArrowIcon from '~public/assets/icons/navigation/smallArrow.svg';
import WebIcon from '~public/assets/icons/navigation/web.svg';
import AvatarLogo from '~public/img/avatarLogo.svg';
import EyeNoneIcon from '~public/img/nsfw/eye-none.svg';
import EyeOpenIcon from '~public/img/nsfw/eye-open.svg';
import { userApi } from '~services/api/user';
import { Dropdown, DropdownElement, NeuronImage } from '~shared/ui/atoms';
import { DropdownSeparator } from '~shared/ui/atoms/Dropdown';
import { ButtonIconStroked, ButtonSecondary } from '~shared/ui/molecules';
import type { AppDispatch } from '~store/index';
import { logout } from '~store/utils/logout';
import { getAppUrl, getSubdomainUrlStr } from '~utils/host';
import { useHeaderContext } from '~widgets/Header/context';

import { PromoHeadButton } from '../PromoHeadButton';
import { ArrowIconContainer } from '../styles';
import { MySubscriptionsItem } from './MySubscriptionsItem';
import { SettingsItem } from './SettingsItem';
import {
	SubscriptionDropdownElement,
	UserProfile,
	UserProfileLeft,
	UserProfileRight,
} from './styles';

export const AuthContent = () => {
	const { recipientId: botClientId } = useParams<{ recipientId?: string }>();
	const dispatch = useDispatch<AppDispatch>();
	const t = useTranslations('ProfileMenu');
	const { language, locale } = useLanguage();
	const {
		userId,
		isDemoUser,
		isSafeMode,
		isSuccess: isSuccessSession,
	} = useSession();
	const updateSafeMode = useSwitchSafeModeWithAgeValidation();
	const isDuplicateOwner = useIsDuplicateOwner();

	const { hasSubscription, isSuccess, balance } = useAccountContext();
	const { languageOpen } = useHeaderContext();
	const { openPaywall } = usePaywallContext();

	const dropdownRef = useRef<HTMLButtonElement | null>(null);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [loadingNSFW, setLoadingNSFW] = useState(false);

	const { data: userInfo } = userApi.useUserInfoQuery(
		{ id: userId },
		{ skip: !userId || !isSuccessSession }
	);
	const { data } = userApi.useGetLevelQuery(
		{ userId },
		{ skip: isDemoUser || !userId || !isSuccessSession }
	);

	const reasonType = annals.checkout.usePaymentReasonType();
	const annalsNavSignOutClicked =
		annals.navigation.useNavTopSignOutButtonClicked();

	const appUrl = getAppUrl();
	const creatorUrl = getSubdomainUrlStr('creator', appUrl);

	const showPremiumPromo = !hasSubscription && !isSafeMode;
	const showNSFWButton = !hasSubscription && isSafeMode;

	const handleSwitchModeClick = async () => {
		try {
			setLoadingNSFW(true);
			await updateSafeMode(!isSafeMode, {
				force: true,
				nsfwReason: NsfwReason.TopBanner,
				registerInbound: RegisterInbound.HeaderNsfwAction,
			});
		} catch (e) {}

		setLoadingNSFW(false);
	};
	const triggerLogout = useCallback(() => {
		annalsNavSignOutClicked();
		dispatch(logout());
	}, [dispatch, annalsNavSignOutClicked]);

	return (
		<>
			{isSuccess && showPremiumPromo && (
				<PromoHeadButton botClientId={botClientId} reasonType={reasonType} />
			)}
			{isSuccess && showNSFWButton && (
				<ButtonSecondary
					data-name="nsfw-button"
					size="M"
					loading={loadingNSFW}
					leftIcon={isSafeMode ? <EyeNoneIcon /> : <EyeOpenIcon />}
					onClick={handleSwitchModeClick}
				>
					{t('nsfwButtonTitle')}
				</ButtonSecondary>
			)}
			{isSuccess && hasSubscription && (
				<ButtonSecondary
					data-name="neurons-button"
					size="M"
					leftIcon={<NeuronImage />}
					onClick={openPaywall({
						reason: PaymentReason.LeftMenu,
						reasonType,
						prefer: 'neurons',
						botClientId,
					})}
				>
					{balance > 0
						? new Intl.NumberFormat().format(balance)
						: t('addNeuronsButtonTitle')}
				</ButtonSecondary>
			)}
			<ButtonIconStroked
				type="button"
				size="M"
				ref={dropdownRef}
				bigIcon={true}
				leftIcon={<AvatarLogo />}
				rightIcon={
					<ArrowIconContainer active={dropdownOpen}>
						<ArrowIcon />
					</ArrowIconContainer>
				}
			/>

			<Dropdown targetRef={dropdownRef} onChange={setDropdownOpen}>
				<UserProfile>
					<UserProfileLeft>
						<Text align="left" size="L">
							{userInfo?.name}
						</Text>
						<SubHeadline
							data-name="level-item"
							size="S"
							color="grey-52-solid"
							align="left"
						>
							{t('levelTitle')} {data?.level}
						</SubHeadline>
					</UserProfileLeft>
					<UserProfileRight>
						<AvatarLogo />
					</UserProfileRight>
				</UserProfile>

				{isSuccess && showPremiumPromo && (
					<SubscriptionDropdownElement
						data-name="subscription-promo-item"
						data-coc={true}
						onClick={openPaywall({
							reason: PaymentReason.LeftMenu,
							reasonType,
							prefer: 'subscription',
							botClientId,
						})}
					>
						<SubHeadline size="S" color="grey-95-solid" align="left">
							{t('upgradeToPremiumTitle')}
						</SubHeadline>
						<Text color="rose-58-solid" size="S" align="left">
							{t('upgradeToPremiumSubtitle')}
						</Text>
					</SubscriptionDropdownElement>
				)}
				{isSuccess && !hasSubscription && (
					<DropdownElement
						data-name="neurons-item"
						icon={<NeuronImage />}
						hint={t('neuronsSubtitle')}
						onClick={openPaywall({
							reason: PaymentReason.LeftMenu,
							reasonType,
							prefer: 'neurons',
							botClientId,
						})}
					>
						<Text size="M" align="left">
							{new Intl.NumberFormat().format(balance)} {t('neuronsTitle')}
						</Text>
					</DropdownElement>
				)}
				{(showPremiumPromo || !showNSFWButton) && (
					<DropdownElement
						data-name="nsfw-item"
						hint={isSafeMode ? t('nsfwShow') : t('nsfwHide')}
						onClick={handleSwitchModeClick}
						icon={isSafeMode ? <EyeNoneIcon /> : <EyeOpenIcon />}
					>
						<Text size="M" align="left">
							{t('nsfwTitle')}
						</Text>
					</DropdownElement>
				)}
				<SettingsItem />

				<MySubscriptionsItem />
				{isDuplicateOwner && (
					<DropdownElement
						data-name="digital-duplicate-item"
						href={creatorUrl}
						icon={<LinkIcon />}
						hint="Manage"
						as="a"
					>
						<Text size="M" align="left">
							{t('digitalDuplicateTitle')}
						</Text>
					</DropdownElement>
				)}
				<DropdownSeparator />
				<DropdownElement
					data-name="language"
					icon={<WebIcon />}
					onClick={languageOpen}
					hint={`${language} - ${locale.toUpperCase()}`}
				>
					<Text size="M" align="left">
						{t('languageTitle')}
					</Text>
				</DropdownElement>
				<DropdownSeparator />
				<DropdownElement
					data-name="sign-out-item"
					icon={<LogoutIcon />}
					onClick={triggerLogout}
				>
					<Text size="M" align="left">
						{t('signOut')}
					</Text>
				</DropdownElement>
			</Dropdown>
		</>
	);
};
